import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  useUpdateOrderStatusMutation,
  useProcessPaymentMutation,
  useCancelOrderMutation,
} from 'state/api';

const ORDER_STATUSES = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_MODES = ['cash', 'card', 'upi', 'netbanking', 'credit'];

const OrderStatus = ({ order }) => {
  const [status, setStatus] = useState(order?.status || 'pending');
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const [updateOrderStatus, { isLoading: updating }] = useUpdateOrderStatusMutation();
  const [processPayment, { isLoading: paying }] = useProcessPaymentMutation();
  const [cancelOrder, { isLoading: cancelling }] = useCancelOrderMutation();

  const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  if (!order) return null;

  const handleUpdateStatus = async () => {
    try {
      await updateOrderStatus({ id: order._id, status }).unwrap();
      notify('Order status updated');
    } catch (err) {
      notify(err?.data?.message || 'Failed to update status', 'error');
    }
  };

  const handleRecordPayment = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      notify('Enter a valid amount', 'error');
      return;
    }
    try {
      await processPayment({ id: order._id, amount: value, paymentMode }).unwrap();
      notify('Payment recorded');
      setAmount('');
    } catch (err) {
      notify(err?.data?.message || 'Failed to record payment', 'error');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelOrder(order._id).unwrap();
      notify('Order cancelled');
    } catch (err) {
      notify(err?.data?.message || 'Failed to cancel order', 'error');
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>Order & Shipping Info</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="orderStatusSelectLabel">Change Order Status</InputLabel>
          <Select
            labelId="orderStatusSelectLabel"
            id="orderStatusSelect"
            label="Change Order Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {ORDER_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          fullWidth
          sx={{ mb: 3 }}
          onClick={handleUpdateStatus}
          disabled={updating}
        >
          {updating ? <CircularProgress size={20} /> : 'Update Status'}
        </Button>

        <Typography variant="h6" sx={{ mb: 1 }}>Record Payment</Typography>
        <TextField
          fullWidth
          label="Amount"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="paymentModeSelectLabel">Payment Mode</InputLabel>
          <Select
            labelId="paymentModeSelectLabel"
            id="paymentModeSelect"
            label="Payment Mode"
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
          >
            {PAYMENT_MODES.map((m) => (
              <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          fullWidth
          sx={{ mb: 3 }}
          onClick={handleRecordPayment}
          disabled={paying}
        >
          {paying ? <CircularProgress size={20} /> : 'Record Payment'}
        </Button>

        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={handleCancel}
          disabled={cancelling}
        >
          {cancelling ? <CircularProgress size={20} /> : 'Cancel Order'}
        </Button>
      </CardContent>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default OrderStatus;
